import { useEffect, useState } from 'react'
import { useAuth as useLogtoAuth } from '../contexts/AuthContext'

/**
 * Custom hook that provides authentication functionality
 * This is a wrapper around the AuthContext that adds additional utility methods
 */
export const useAuth = () => {
  const auth = useLogtoAuth()
  const [userClaims, setUserClaims] = useState<any>(null)

  // Fetch user claims when authenticated
  useEffect(() => {
    if (auth.isAuthenticated) {
      // Get user claims
      auth.getIdTokenClaims()
        .then(claims => {
          setUserClaims(claims)
        })
        .catch(err => {
          console.error('Failed to get user claims:', err)
          setUserClaims(null)
        })
    } else {
      setUserClaims(null)
    }
  }, [auth.isAuthenticated, auth.getIdTokenClaims])

  // Get user profile from token
  const getUserProfile = async () => {
    if (!auth.isAuthenticated) {
      return null
    }
    return userClaims
  }

  // Enhanced login with error handling
  const login = async () => {
    try {
      await auth.signIn()
    } catch (err) {
      console.error('Login failed:', err)
      throw err
    }
  }

  // Enhanced logout with cleanup
  const logout = async () => {
    try {
      await auth.signOut()
    } catch (err) {
      console.error('Logout failed:', err)
      throw err
    }
  }

  // Check if user has specific role or organization
  const hasRole = (role: string): boolean => {
    if (!userClaims) return false
    return userClaims.roles?.includes(role) || false
  }

  const hasOrganization = (orgId: string): boolean => {
    if (!userClaims) return false
    return userClaims.organizations?.some((org: any) => org.id === orgId) || false
  }

  // Create authenticated fetch function that gets token fresh each time
  const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const token = await auth.getAccessToken()
    
    if (!token) {
      throw new Error('No access token available')
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    }

    return fetch(url, {
      ...options,
      headers,
    })
  }

  return {
    // Core auth state
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    error: auth.error,
    user: userClaims,
    
    // Actions
    login,
    logout,
    
    // Token methods
    getAccessToken: auth.getAccessToken,
    getIdTokenClaims: auth.getIdTokenClaims,
    
    // Utility methods
    authenticatedFetch,
    getUserProfile,
    hasRole,
    hasOrganization,
  }
}

export default useAuth 